import React, { useState, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Image,
  Alert,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import creating from "../assets/creating.svg";
import {
  BookListAccordion,
  NewBookAccordion,
} from "../components/AccordionComponent";
import "../common.css";
import { createList, uploadBookClubPhoto, deleteBookClubPhoto } from "../api";
import { buildDateString, compressImage } from "../utils";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import Loading from "../components/Loading";

function CreateList() {
  const [newTitle, setNewTitle] = useState("");
  const [newAuthor, setNewAuthor] = useState("");
  const [newPageCount, setNewPageCount] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newList, setNewList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withImage, setWithImage] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setCompressing(true);
      try {
        const compressedFile = await compressImage(file, 1);
        setImageFile(compressedFile);
        setImagePreview(URL.createObjectURL(compressedFile));
      } catch (error) {
        console.error("Error compressing image:", error);
      } finally {
        setCompressing(false);
      }
    }
  };

  const createNewList = async () => {
    if (withImage && !imageFile) {
      return;
    }

    setUploading(true);

    let uploadedImageUrl = null;

    try {
      if (withImage && imageFile) {
        uploadedImageUrl = await uploadBookClubPhoto(imageFile);
      }

      const curr_date = new Date();
      const listObj = {
        name: buildDateString(curr_date),
        created: curr_date.toISOString(),
        book_list: newList,
        closed: false,
        ...(uploadedImageUrl && { imageUrl: uploadedImageUrl }),
      };

      try {
        await createList(listObj);
      } catch (error) {
        if (uploadedImageUrl) {
          await deleteBookClubPhoto(uploadedImageUrl);
        }
        throw error;
      }

      setNewTitle("");
      setNewAuthor("");
      setNewDescription("");
      setNewPageCount("");
      setNewList([]);
      setWithImage(true);
      setImageFile(null);
      setImagePreview(null);
      navigate("/");
    } catch (error) {
      console.error("Error creating list:", error);
    } finally {
      setUploading(false);
    }
  };

  const addBook = () => {
    const newListItem = {
      title: newTitle,
      author: newAuthor,
      description: newDescription,
      pageCount: newPageCount,
    };
    setNewList((prevList) => [...prevList, newListItem]);
    setNewTitle("");
    setNewAuthor("");
    setNewDescription("");
    setNewPageCount("");
  };

  const removeBookAtIndex = (index) => {
    setNewList((prevList) => [
      ...prevList.slice(0, index),
      ...prevList.slice(index + 1),
    ]);
  };

  const canCreateList =
    newList.length !== 0 && (!withImage || (withImage && imageFile));

  const pageImage = (
    <Image
      src={creating}
      className="main-picture"
      onLoad={() => {
        if (loading) {
          setLoading(false);
        }
      }}
    />
  );

  if (loading) {
    return <Loading pageImage={pageImage} />;
  }

  return (
    <Container fluid>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="centered-container">{pageImage}</div>
        </Col>
      </Row>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="title">Create Book List</div>
        </Col>
      </Row>
      <Row className="mt-1">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="subtitle">Add a new book</div>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <NewBookAccordion
            newTitle={newTitle}
            setNewTitle={setNewTitle}
            newAuthor={newAuthor}
            setNewAuthor={setNewAuthor}
            newDescription={newDescription}
            setNewDescription={setNewDescription}
            newPageCount={newPageCount}
            setNewPageCount={setNewPageCount}
            addBook={addBook}
          />
        </Col>
      </Row>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="subtitle">Current list</div>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          {(newList.length === 0 && (
            <div>
              <Alert className="info-alert">
                <div>The book list currently has no books.</div>
                <div className="mt-3">
                  Start adding books to the list using the dropdown above.
                </div>
              </Alert>
            </div>
          )) || (
            <BookListAccordion
              bookList={newList}
              deletable={true}
              removeBookAtIndex={removeBookAtIndex}
              ranked={false}
            />
          )}
        </Col>
      </Row>
      <Row className="mt-4">
        <Col
          xs={{ span: 10, offset: 1 }}
          sm={{ span: 8, offset: 2 }}
          md={{ span: 6, offset: 3 }}
        >
          <div className="centered-container">
            <Form.Check
              type="switch"
              id="with-image-switch"
              label="List with Image"
              checked={withImage}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setWithImage(isChecked);
                if (!isChecked) {
                  setImageFile(null);
                  setImagePreview(null);
                }
              }}
              className="image-toggle"
            />
          </div>
        </Col>
      </Row>
      {withImage && (
        <Row className="mt-3">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div className="centered-container">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
              <Button
                className="button-tertiary"
                onClick={() => fileInputRef.current?.click()}
                disabled={compressing}
              >
                {compressing ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Processing...
                  </>
                ) : imageFile ? (
                  "Change Photo"
                ) : (
                  "Select Photo"
                )}
              </Button>
            </div>
            {imagePreview && (
              <div className="mt-3 centered-container">
                <div className="clubbers-photo-frame">
                  <Image src={imagePreview} fluid className="clubbers-photo" />
                </div>
              </div>
            )}
            {!imageFile && !compressing && (
              <Alert className="warning-alert mt-3">
                Please upload a photo to create this list.
              </Alert>
            )}
          </Col>
        </Row>
      )}
      {canCreateList && (
        <Row className="mt-4">
          <Col
            xs={{ span: 10, offset: 1 }}
            sm={{ span: 8, offset: 2 }}
            md={{ span: 6, offset: 3 }}
          >
            <div className="centered-container">
              <Button
                className="button-tertiary"
                onClick={createNewList}
                disabled={uploading}
              >
                {uploading ? "Creating..." : "Create List"}
              </Button>
            </div>
          </Col>
        </Row>
      )}
      <Footer />
    </Container>
  );
}

export default CreateList;
